import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import {
  exchangeRateApi,
  FINExchangeRate,
  FINCurr,
  ACCSetting,
} from '../../../services/exchangeRateApi';

export type Mode = 'browse' | 'add' | 'edit';

interface FormFields {
  f10e_01: string;
  f10e_02: string;
  f10e_12: number;
  f10e_11: number;
  f10e_13: number;
  f10e_14: number;
  exp_customs_rate: number;
  is_active: boolean;
  is_valid: boolean;
}

const emptyForm: FormFields = {
  f10e_01: '',
  f10e_02: new Date().toISOString().slice(0, 10),
  f10e_12: 0,
  f10e_11: 0,
  f10e_13: 0,
  f10e_14: 0,
  exp_customs_rate: 0,
  is_active: true,
  is_valid: false,
};

export function useExchangeRateForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FINExchangeRate[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormFields>({ ...emptyForm });
  const [currencies, setCurrencies] = useState<FINCurr[]>([]);
  const [settings, setSettings] = useState<ACCSetting | null>(null);

  // Search filters
  const [searchCurrency, setSearchCurrency] = useState('');
  const [searchDate, setSearchDate] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  // Get the RateUnitValue for current currency
  const rateUnitValue = current?.rate_unit_value ?? currencies.find(c => c.curr_code === form.f10e_01)?.rate_unit_value ?? 0;

  // Load records
  const loadRecords = useCallback(async (p = 1, currency?: string, date?: string) => {
    setLoading(true);
    try {
      const res = await exchangeRateApi.list(p, pageSize, currency, date);
      setRecords(res.data || []);
      setTotal(res.total);
      setPage(p);
      return res.data || [];
    } catch {
      message.error('載入失敗');
      return [];
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  // Load currencies + settings on mount
  useEffect(() => {
    exchangeRateApi.listCurrencies().then(setCurrencies).catch(() => {});
    exchangeRateApi.getSettings().then(setSettings).catch(() => {});
  }, []);

  // Initial load
  useEffect(() => {
    loadRecords().then(data => {
      if (data.length > 0) setCurrentIdx(0);
    });
  }, [loadRecords]);

  // Sync form fields from current record
  useEffect(() => {
    if (current && mode === 'browse') {
      setForm({
        f10e_01: current.f10e_01,
        f10e_02: current.f10e_02?.slice(0, 10) || '',
        f10e_12: current.f10e_12,
        f10e_11: current.f10e_11,
        f10e_13: current.f10e_13,
        f10e_14: current.f10e_14,
        exp_customs_rate: current.exp_customs_rate,
        is_active: current.is_active,
        is_valid: current.is_valid,
      });
    }
  }, [current, mode]);

  // Set a form field
  const setField = useCallback(<K extends keyof FormFields>(key: K, value: FormFields[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  // BR-009: Auto-fill when currency changes in add mode
  const handleCurrencyChange = useCallback(async (currCode: string) => {
    setField('f10e_01', currCode);
    if (mode === 'add' || mode === 'edit') {
      try {
        const latest = await exchangeRateApi.getLatestRate(currCode);
        setForm(prev => ({
          ...prev,
          f10e_01: currCode,
          f10e_12: latest.f10e_12,
          f10e_11: latest.f10e_11,
          f10e_13: latest.f10e_13,
          exp_customs_rate: latest.exp_customs_rate,
        }));
      } catch {
        // No previous rate — keep zeros
      }
    }
  }, [mode, setField]);

  // Navigation
  const selectRecord = (idx: number) => {
    if (!isEditing && idx >= 0 && idx < records.length) setCurrentIdx(idx);
  };

  // Search
  const doSearch = useCallback(async () => {
    const data = await loadRecords(1, searchCurrency || undefined, searchDate || undefined);
    if (data.length > 0) setCurrentIdx(0);
    else setCurrentIdx(-1);
  }, [loadRecords, searchCurrency, searchDate]);

  const doPageChange = useCallback(async (newPage: number) => {
    const data = await loadRecords(newPage, searchCurrency || undefined, searchDate || undefined);
    if (data.length > 0) setCurrentIdx(0);
    else setCurrentIdx(-1);
  }, [loadRecords, searchCurrency, searchDate]);

  // Start add mode (BR-004)
  const startAdd = () => {
    setMode('add');
    setForm({ ...emptyForm, f10e_02: new Date().toISOString().slice(0, 10) });
  };

  // Start edit mode
  const startEdit = () => {
    if (!current) return;
    setMode('edit');
  };

  // Cancel
  const cancel = () => {
    setMode('browse');
    if (current) {
      setForm({
        f10e_01: current.f10e_01,
        f10e_02: current.f10e_02?.slice(0, 10) || '',
        f10e_12: current.f10e_12,
        f10e_11: current.f10e_11,
        f10e_13: current.f10e_13,
        f10e_14: current.f10e_14,
        exp_customs_rate: current.exp_customs_rate,
        is_active: current.is_active,
        is_valid: current.is_valid,
      });
    }
  };

  // Save
  const save = async () => {
    try {
      if (mode === 'add') {
        await exchangeRateApi.create({
          f10e_01: form.f10e_01,
          f10e_02: form.f10e_02,
          f10e_12: form.f10e_12,
          f10e_11: form.f10e_11,
          f10e_13: form.f10e_13,
          f10e_14: form.f10e_14,
          exp_customs_rate: form.exp_customs_rate,
          is_active: form.is_active,
          is_valid: form.is_valid,
        });
        message.success('新增成功');
      } else if (mode === 'edit' && current) {
        await exchangeRateApi.update(current.f10e_01, current.f10e_02?.slice(0, 10) || '', {
          f10e_12: form.f10e_12,
          f10e_11: form.f10e_11,
          f10e_13: form.f10e_13,
          f10e_14: form.f10e_14,
          exp_customs_rate: form.exp_customs_rate,
          is_active: form.is_active,
          is_valid: form.is_valid,
        });
        message.success('修改成功');
      }
      setMode('browse');
      await loadRecords(page, searchCurrency || undefined, searchDate || undefined);
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) {
        vErrs.forEach((e: any) => message.error(e.message));
      } else {
        message.error(err?.response?.data?.error || '儲存失敗');
      }
    }
  };

  // Delete
  const doDelete = async () => {
    if (!current) return;
    try {
      await exchangeRateApi.delete(current.f10e_01, current.f10e_02?.slice(0, 10) || '');
      message.success('刪除成功');
      const data = await loadRecords(page, searchCurrency || undefined, searchDate || undefined);
      if (data.length > 0) {
        setCurrentIdx(Math.min(currentIdx, data.length - 1));
      } else {
        setCurrentIdx(-1);
      }
    } catch {
      message.error('刪除失敗');
    }
  };

  return {
    mode, isEditing, loading,
    records, total, page, pageSize, current, currentIdx,
    form, setField, rateUnitValue,
    currencies, settings,
    searchCurrency, setSearchCurrency, searchDate, setSearchDate,
    selectRecord, doSearch, doPageChange,
    startAdd, startEdit, cancel, save, doDelete,
    handleCurrencyChange,
  };
}
